import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, User, Package, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TaskTimeline } from '@/components/tracking/TaskTimeline';
import { formatInTimeZone } from 'date-fns-tz';

interface PublicTaskData {
  id: string;
  order_number?: string;
  customer_name?: string;
  address?: string;
  status: string;
  time_slot_start?: string;
  time_slot_end?: string;
  tenant: {
    name: string;
    brand_primary?: string;
    logo_url?: string;
  };
  driver_profiles?: Array<{
    profiles: { display_name: string };
  }>;
}

const PublicTrackingPage: React.FC = () => {
  const { trackingId } = useParams<{ trackingId: string }>();
  const [task, setTask] = useState<PublicTaskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantTimezone, setTenantTimezone] = useState('Asia/Singapore');

  useEffect(() => {
    if (trackingId) {
      fetchTaskData();
    }
  }, [trackingId]);

  const fetchTaskData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch task with minimal data for public view
      const { data: taskData, error: taskError } = await (supabase as any)
        .from('tasks')
        .select(`
          id,
          order_number,
          customer_name,
          address,
          status,
          time_slot_start,
          time_slot_end,
          tenants!inner(
            name,
            brand_primary,
            logo_url
          ),
          driver_profiles!left(
            profiles!inner(display_name)
          )
        `)
        .eq('id', trackingId)
        .single();

      if (taskError) {
        if (taskError.code === 'PGRST116') {
          setError('Tracking information not found.');
        } else {
          throw taskError;
        }
        return;
      }

      setTask({
        id: taskData.id,
        order_number: taskData.order_number,
        customer_name: taskData.customer_name,
        address: taskData.address,
        status: taskData.status,
        time_slot_start: taskData.time_slot_start,
        time_slot_end: taskData.time_slot_end,
        tenant: taskData.tenants,
        driver_profiles: Array.isArray(taskData.driver_profiles) ? taskData.driver_profiles : []
      });
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      setError('Unable to load tracking information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'en_route':
        return <Package className="w-5 h-5 text-blue-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'en_route':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'accepted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Tracking Not Available</h2>
            <p className="text-muted-foreground">
              {error || 'The tracking information you requested could not be found.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center gap-4">
            {task.tenant.logo_url ? (
              <img 
                src={task.tenant.logo_url} 
                alt={task.tenant.name}
                className="w-10 h-10 rounded-lg object-contain"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: task.tenant.brand_primary || 'hsl(var(--primary))' }}
              >
                <Package className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Track Your Delivery</h1>
              <p className="text-muted-foreground">{task.tenant.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="space-y-6">
          {/* Delivery Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {getStatusIcon(task.status)}
                <div>
                  <div className="flex items-center gap-2">
                    {task.order_number && (
                      <span>Order #{task.order_number}</span>
                    )}
                    <Badge className={`${getStatusColor(task.status)} capitalize`}>
                      {task.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  {task.customer_name && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Delivery for {task.customer_name}
                    </p>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {task.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Delivery Address</p>
                      <p className="text-sm text-muted-foreground">{task.address}</p>
                    </div>
                  </div>
                )}
                
                {task.time_slot_start && task.time_slot_end && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Time Window</p>
                  <p className="text-sm text-muted-foreground">
                    {formatInTimeZone(new Date(task.time_slot_start), tenantTimezone, 'h:mm a')} - {formatInTimeZone(new Date(task.time_slot_end), tenantTimezone, 'h:mm a')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatInTimeZone(new Date(task.time_slot_start), tenantTimezone, 'EEEE, MMMM d, yyyy')}
                  </p>
                    </div>
                  </div>
                )}
                
                {task.driver_profiles?.[0] && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Your Driver</p>
                      <p className="text-sm text-muted-foreground">
                        {task.driver_profiles[0].profiles.display_name}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status Messages */}
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                {task.status === 'pending' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Order Confirmed</h3>
                    <p className="text-muted-foreground">
                      Your delivery has been confirmed and will be assigned to a driver soon.
                    </p>
                  </div>
                )}
                {task.status === 'accepted' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Driver Assigned</h3>
                    <p className="text-muted-foreground">
                      A driver has been assigned to your delivery and will be on their way soon.
                    </p>
                  </div>
                )}
                {task.status === 'en_route' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">On the Way</h3>
                    <p className="text-muted-foreground">
                      Your driver is on the way to your location.
                    </p>
                  </div>
                )}
                {task.status === 'arrived' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Driver Arrived</h3>
                    <p className="text-muted-foreground">
                      Your driver has arrived at your location.
                    </p>
                  </div>
                )}
                {task.status === 'completed' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-green-700">Delivery Complete</h3>
                    <p className="text-muted-foreground">
                      Your delivery has been completed successfully. Thank you for choosing {task.tenant.name}!
                    </p>
                  </div>
                )}
                {task.status === 'failed' && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2 text-red-700">Delivery Issue</h3>
                    <p className="text-muted-foreground">
                      There was an issue with your delivery. Please contact {task.tenant.name} for assistance.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <TaskTimeline taskId={task.id} />
        </div>
      </div>
    </div>
  );
};

export default PublicTrackingPage;